import React, { useState, useEffect } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const FractionPizzaGame = ({ gameData, onComplete }) => {
    const [currentPizzaIndex, setCurrentPizzaIndex] = useState(0);
    const [selectedSlices, setSelectedSlices] = useState([]);
    const [feedback, setFeedback] = useState(null);
    const [score, setScore] = useState(0);

    const currentPizza = gameData?.pizzas?.[currentPizzaIndex];

    useEffect(() => {
        if (currentPizza) {
            setSelectedSlices([]);
            setFeedback(null);
        }
    }, [currentPizzaIndex, currentPizza]);

    const toggleSlice = (sliceIndex) => {
        if (feedback) return;
        triggerHaptic(30);

        if (selectedSlices.includes(sliceIndex)) {
            setSelectedSlices(prev => prev.filter(i => i !== sliceIndex));
        } else {
            setSelectedSlices(prev => [...prev, sliceIndex]);
        }
    };

    const checkAnswer = () => {
        if (feedback || selectedSlices.length === 0) return;
        const isCorrect = selectedSlices.length === currentPizza.numerator;

        if (isCorrect) {
            setFeedback('correct');
            const points = gameData.points_per_pizza || 25;
            const newScore = score + points;
            setScore(newScore);
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                if (currentPizzaIndex + 1 >= gameData.pizzas.length) {
                    triggerHaptic([200, 100, 200, 100, 300]);
                    onComplete?.(newScore);
                } else {
                    setCurrentPizzaIndex(prev => prev + 1);
                }
            }, 4000);
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => {
                setFeedback(null);
                setSelectedSlices([]);
            }, 2000);
        }
    };

    const renderPizzaSlice = (index, total) => {
        const angle = 360 / total;
        const rotation = angle * index;
        const isSelected = selectedSlices.includes(index);

        const startAngle = (rotation - 90) * (Math.PI / 180);
        const endAngle = (rotation + angle - 90) * (Math.PI / 180);
        const radius = 85;
        const centerX = 100;
        const centerY = 100;

        const x1 = centerX + radius * Math.cos(startAngle);
        const y1 = centerY + radius * Math.sin(startAngle);
        const x2 = centerX + radius * Math.cos(endAngle);
        const y2 = centerY + radius * Math.sin(endAngle);

        const largeArcFlag = angle > 180 ? 1 : 0;
        const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

        return (
            <path
                key={index}
                d={pathData}
                fill={isSelected ? "#F59E0B" : "#FDE68A"}
                stroke="#78350F"
                strokeWidth="1.5"
                onClick={() => toggleSlice(index)}
                className={`cursor-pointer transition-all duration-300 transform origin-center ${isSelected ? 'scale-[1.05] drop-shadow-[0_0_15px_rgba(245,158,11,0.6)]' : 'hover:scale-[1.02] hover:brightness-105 active:scale-95'}`}
            />
        );
    };

    if (!currentPizza) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-orange-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">🍕</div>
                <h3 className="text-base md:text-3xl font-black text-orange-900 tracking-tighter uppercase">Kepame picas...</h3>
            </div>
        );
    }

    const progress = (currentPizzaIndex / gameData.pizzas.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-orange-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-orange-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        🍕
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Trupmėnų Pica
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Meistrystė</p>
                        <p className="text-sm md:text-2xl font-black text-orange-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Užsakymas</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentPizzaIndex + 1}/{gameData.pizzas.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-orange-400 via-red-500 to-rose-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(249,115,22,0.3)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-12">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-14 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl text-center relative overflow-hidden flex flex-col items-center">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-6xl md:text-[150px] opacity-[0.03] select-none pointer-events-none">👨‍🍳</div>
                
                <div className="w-full max-w-4xl space-y-4 md:space-y-8 relative z-10">
                    <div className="space-y-2 md:space-y-4">
                        <h4 className="text-[8px] md:text-[10px] font-black text-orange-400 uppercase tracking-[0.5em] mb-2 md:mb-4">Kliento Pageidavimas</h4>
                        <h3 className="text-base md:text-4xl font-black text-slate-800 tracking-tighter leading-tight uppercase">
                            {currentPizza.instruction}
                        </h3>
                    </div>

                    <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
                        {/* Order HUD */}
                        <div className="bg-white rounded-2xl md:rounded-[3rem] p-6 md:p-10 border-4 md:border-8 border-orange-500 shadow-2xl animate-pop-in relative overflow-hidden group w-full max-w-[200px]">
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(249,115,22,0.05)_0%,transparent_100%)] pointer-events-none" />
                            <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 md:mb-4">Trupmėna</p>
                            <div className="text-center relative z-10">
                                <div className="text-5xl md:text-8xl font-black text-orange-600 tracking-tighter font-serif leading-none border-b-4 md:border-b-8 border-orange-100 inline-block px-4 md:px-6 pb-2 mb-2">
                                    {currentPizza.numerator}
                                </div>
                                <div className="text-2xl md:text-5xl font-black text-slate-300 tracking-tighter font-serif leading-none mt-2">
                                    {currentPizza.total_slices}
                                </div>
                            </div>
                        </div>

                        <div className="premium-glass bg-white/60 rounded-xl md:rounded-[2.5rem] p-4 md:p-8 border-2 border-orange-100 space-y-4 md:space-y-6 shadow-inner w-full max-w-[200px]">
                            <div className="space-y-1">
                                <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest">Dalių</p>
                                <div className="flex items-center justify-center gap-2 md:gap-4">
                                    <span className={`text-xl md:text-5xl font-black font-mono transition-all duration-300 ${selectedSlices.length === currentPizza.numerator ? 'text-emerald-500 scale-110' : 'text-slate-800'}`}>
                                        {selectedSlices.length}
                                    </span>
                                    <span className="text-sm md:text-2xl font-black text-slate-200">/</span>
                                    <span className="text-base md:text-3xl font-black text-slate-300">{currentPizza.numerator}</span>
                                </div>
                            </div>

                            <div className="flex gap-1 md:gap-2 justify-center flex-wrap">
                                {Array.from({ length: currentPizza.numerator }).map((_, i) => (
                                    <div 
                                        key={i} 
                                        className={`
                                            w-3 h-3 md:w-5 md:h-5 rounded-md md:rounded-lg transition-all duration-500 
                                            ${i < selectedSlices.length ? 'bg-orange-500 scale-125 shadow-[0_0_15px_rgba(249,115,22,0.6)]' : 'bg-slate-200'}
                                        `}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="flex flex-col items-center gap-4 md:gap-8 w-full max-w-4xl mx-auto">
            {/* Pizza Visualizer Container */}
            <div className="relative group scale-75 md:scale-100">
                <div className="absolute inset-0 bg-orange-500/10 rounded-full blur-[100px] pointer-events-none group-hover:bg-orange-500/20 transition-all duration-700"></div>
                <svg 
                    width="260" 
                    height="260" 
                    viewBox="0 0 200 200" 
                    className="drop-shadow-[0_40px_80px_rgba(120,53,15,0.4)] relative z-10 md:scale-125 transition-transform duration-700"
                >
                    <circle cx="100" cy="100" r="95" fill="#78350F" />
                    <circle cx="100" cy="100" r="92" fill="#D97706" />
                    <circle cx="100" cy="100" r="85" fill="#FEF3C7" stroke="#78350F" strokeWidth="1" />
                    
                    <g className="transition-all duration-500">
                        {Array.from({ length: currentPizza.total_slices }).map((_, index) =>
                            renderPizzaSlice(index, currentPizza.total_slices)
                        )}
                    </g>
                    
                    <circle cx="100" cy="100" r="4" fill="#78350F" opacity="0.3" />
                </svg>
            </div>

            <button
                onClick={checkAnswer}
                disabled={!!feedback || selectedSlices.length === 0}
                className={`
                    premium-button-3d w-full py-4 md:py-10 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl md:rounded-[3rem] font-black text-base md:text-3xl shadow-2xl uppercase tracking-widest transition-all border-b-8 md:border-b-[12px] border-orange-800
                    ${selectedSlices.length === currentPizza.numerator ? 'scale-[1.02] active:translate-y-2 active:border-b-0' : 'opacity-40'}
                `}
            >
                PATIEKTI PICĄ ✓
            </button>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-6 md:p-12 rounded-[2.5rem] md:rounded-[4rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback === 'correct' ? '🍕' : '👨‍🍳'}
                        </div>
                        
                        <div className="text-center space-y-4 pt-4">
                            <h2 className={`text-xl md:text-5xl font-black uppercase tracking-tighter ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback === 'correct' ? 'SKANI TRUPMĖNA!' : 'KLAIDA RECEPTŪROJE!'}
                            </h2>

                            <div className={`p-4 md:p-10 rounded-xl md:rounded-[3rem] border-2 text-center italic font-serif leading-relaxed ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-sm md:text-2xl font-bold text-slate-700 leading-relaxed italic text-center">
                                    {feedback === 'correct' ? 'Puikiai suskaičiavai picos dalis! Klientas patenkintas.' : 'Oi! Panašu, kad atpjovei netinkamą kiekį dalių.'}
                                </p>
                            </div>

                            <div className="flex items-center justify-center gap-4">
                                <div className="flex gap-2">
                                    <div className="w-2 h-2 md:w-3 md:h-3 bg-slate-200 rounded-full animate-bounce" />
                                    <div className="w-2 h-2 md:w-3 md:h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]" />
                                    <div className="w-2 h-2 md:w-3 md:h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]" />
                                </div>
                                <span className="text-[10px] md:text-sm font-black text-slate-300 uppercase tracking-[0.3em]">
                                    {feedback === 'correct' ? 'Kepame kitą...' : 'Tęsiame...'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default FractionPizzaGame;